import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ShareIcon, ClipboardDocumentIcon, CheckIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

export default function ShareLinkModal({ conference, handleClose }) {
    const [open, setOpen] = useState(true);
    const [copied, setCopied] = useState(false);
    const { t } = useTranslation();

    const closeModal = () => {
        setOpen(false);
        handleClose && handleClose();
    };

    // Get current language from i18n
    const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
    
    const shareLink = conference 
        ? `${window.location.origin}/waiting-room.html?conference_id=${conference.id}&lang=${currentLanguage}`
        : '';

    const copyToClipboard = () => {
        navigator.clipboard.writeText(shareLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = shareLink;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={closeModal}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div>
                                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                                        <ShareIcon
                                            className="h-6 w-6 text-gray-600"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-5">
                                        <Dialog.Title
                                            as="h3"
                                            className="text-lg font-medium leading-6 text-gray-900"
                                        >
                                            {t('VideoConference.ShareLinkModal.Title')}
                                        </Dialog.Title>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                {t('VideoConference.ShareLinkModal.Description')}
                                            </p>
                                        </div>

                                        {/* Conference Title */}
                                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                            <p className="text-sm font-medium text-gray-900">
                                                {conference?.title}
                                            </p>
                                        </div>

                                        {/* Share Link */}
                                        <div className="mt-4">
                                            <label className="block text-xs font-medium text-gray-700 text-left mb-1">
                                                {t('VideoConference.ShareLinkModal.WaitingRoomLabel')}
                                            </label>
                                            <div className="flex items-center">
                                                <input
                                                    type="text"
                                                    value={shareLink}
                                                    readOnly
                                                    className="flex-1 block w-full rounded-l-md border-gray-300 text-sm bg-gray-50 text-gray-600 px-3 py-2 border focus:outline-none"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={copyToClipboard}
                                                    className={`inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md text-sm font-medium transition-colors ${
                                                        copied 
                                                            ? 'bg-green-50 text-green-700 border-green-300' 
                                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    {copied ? (
                                                        <>
                                                            <CheckIcon className="h-4 w-4 mr-1" />
                                                            {t('VideoConference.ShareLinkModal.CopySuccessButton')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                                                            {t('VideoConference.ShareLinkModal.CopyButton')}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Instructions */}
                                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                            <p className="text-xs text-blue-800">
                                                <strong>{t('VideoConference.ShareLinkModal.InstructionsLabel')}</strong> {t('VideoConference.ShareLinkModal.Instructions')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-black px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:text-sm"
                                        onClick={copyToClipboard}
                                    >
                                        {copied ? t('VideoConference.ShareLinkModal.LinkCopiedButton') : t('VideoConference.ShareLinkModal.CopyLinkButton')}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:mt-0 sm:text-sm"
                                        onClick={closeModal}
                                    >
                                        {t('VideoConference.ShareLinkModal.CloseButton')}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}